import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  InputSignal,
  model,
  signal,
  Signal,
  untracked,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { AlertComponent, ButtonComponent, MbsPopupType, MbsSize, ModalSettings, RadioComponent, RadioDescription } from 'mbs-ui-kit';
import { ComputerModalsFacade } from 'mbs-ui-shared@facades/computer.modals.facade';
import { AgentVersionForUseTokenString, RestoreTypeFromStorageTab } from 'mbs-ui-shared@models/backup/storages-type';
import Computer, { AgentType, OsType, PlatformTypes } from 'mbs-ui-shared@models/Computer';
import { BetaFeatureBuildName } from 'mbs-ui-shared@services/beta-request.service';
import { take } from 'rxjs/operators';
import { SelectTargetComputerDefaultValue, SelectTargetComputerStepType } from 'wizards/models/select-target-computer-models';
import { CloudRestoreTypes, RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WindowsFeatures, WizardStepsService } from 'wizards/services/wizard-steps.service';

const AgentVersionForUseToken = 875;
const BetaFeatureNameForRestoreToDifferentComputer = 'restore-to-different-computer';

type HyperVCheckState = { checking: boolean; installed: boolean };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AlertComponent, I18NextPipe, ButtonComponent, RadioComponent, RadioDescription, FormsModule],
  standalone: true,
  selector: 'mbs-select-target-computer-step',
  templateUrl: './select-target-computer-step.component.html'
})
export class SelectTargetComputerStepComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly mainService = inject(RemoteManagementWizardsService);
  private readonly modalFacade = inject(ComputerModalsFacade);
  private readonly stepService = inject(WizardStepsService);

  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;

  public readonly mbsPopupType = MbsPopupType;
  public readonly restoreTypeFromStorageTab = RestoreTypeFromStorageTab;

  private readonly cloudTypes: RestoreAsDiskEnum[] = CloudRestoreTypes;
  private readonly instantTypes = [RestoreAsDiskEnum.InstantRestoreToProxmox, RestoreAsDiskEnum.InstantRestoreToHyperV];
  private readonly hyperVTypes = [RestoreAsDiskEnum.RestoreToHyperV, RestoreAsDiskEnum.InstantRestoreToHyperV];
  private readonly restoreTypeTextMap = {
    [RestoreAsDiskEnum.Physical]: this.i18nPipe.transform('wizards:physical_disk_label'),
    [RestoreAsDiskEnum.Virtual]: this.i18nPipe.transform('wizards:virtual_disk_label'),
    [RestoreAsDiskEnum.RestoreToVMwareVSphere]: this.i18nPipe.transform('wizards:typeData.RestoreToVMwareVSphere.title'),
    [RestoreAsDiskEnum.RestoreToProxmox]: this.i18nPipe.transform('wizards:typeData.RestoreToProxmox.title'),
    [RestoreAsDiskEnum.RestoreToHyperV]: this.i18nPipe.transform('wizards:typeData.RestoreToHyperV.title'),
    [RestoreAsDiskEnum.InstantRestoreToProxmox]: this.i18nPipe.transform('wizards:typeData.instantRestoreToProxmox.title'),
    [RestoreAsDiskEnum.InstantRestoreToHyperV]: this.i18nPipe.transform('wizards:typeData.instantRestoreToHyperV.title'),
    [RestoreAsDiskEnum.EC2]: this.i18nPipe.transform('wizards:typeData.ec2Restore.title'),
    [RestoreAsDiskEnum.AVM]: this.i18nPipe.transform('wizards:typeData.awsRestore.title')
  };
  private readonly agentVersionRequirement = this.i18nPipe.transform('wizards:ibbRestoreTypeWizard.requirements.agentVersion');
  private readonly vmLicenseRequirement = this.i18nPipe.transform('wizards:ibbRestoreTypeWizard.requirements.vmLicense');
  private readonly cloudLicenseRequirement = this.i18nPipe.transform('wizards:ibbRestoreTypeWizard.requirements.cloudRestoreLicense');
  private readonly windowsOsRequirement = this.i18nPipe.transform('wizards:ibbRestoreTypeWizard.requirements.windowsOS');
  private readonly windowsOrNet10OsRequirement = this.i18nPipe.transform('wizards:ibbRestoreTypeWizard.requirements.windowsOrNet10OS');

  private readonly requirementsTextMap: Partial<Record<RestoreAsDiskEnum, string[]>> = {
    [RestoreAsDiskEnum.Physical]: [this.windowsOsRequirement, this.agentVersionRequirement],
    [RestoreAsDiskEnum.Virtual]: [this.windowsOsRequirement, this.agentVersionRequirement],

    [RestoreAsDiskEnum.RestoreToVMwareVSphere]: [this.vmLicenseRequirement, this.windowsOrNet10OsRequirement, this.agentVersionRequirement],
    [RestoreAsDiskEnum.RestoreToProxmox]: [this.vmLicenseRequirement, this.windowsOrNet10OsRequirement, this.agentVersionRequirement],
    [RestoreAsDiskEnum.RestoreToHyperV]: [this.vmLicenseRequirement, this.windowsOsRequirement, this.agentVersionRequirement],

    [RestoreAsDiskEnum.InstantRestoreToProxmox]: [
      this.vmLicenseRequirement,
      this.windowsOrNet10OsRequirement,
      this.agentVersionRequirement
    ],
    [RestoreAsDiskEnum.InstantRestoreToHyperV]: [this.vmLicenseRequirement, this.windowsOsRequirement, this.agentVersionRequirement],

    [RestoreAsDiskEnum.EC2]: [this.cloudLicenseRequirement],
    [RestoreAsDiskEnum.AVM]: [this.cloudLicenseRequirement]
  };

  private readonly computerIncorrect = this.i18nPipe.transform('shared:sidepanel-backup.backupStorages.browseStorage.computerIncorrect');
  private readonly computersNotFound = this.i18nPipe.transform('shared:sidepanel-backup.backupStorages.browseStorage.computersNotFound');

  private readonly computerValidator = (comp: Computer) =>
    comp?.online &&
    Computer.isAgentOnline(comp, AgentType.Backup) &&
    Computer.IsSupportedAgentVersion(comp, AgentType.Backup, AgentVersionForUseToken);

  public readonly value = model<SelectTargetComputerStepType>(SelectTargetComputerDefaultValue);

  public restoreAs: InputSignal<RestoreAsDiskEnum> = input<RestoreAsDiskEnum>(RestoreAsDiskEnum.Physical);

  public restoreTo: WritableSignal<RestoreTypeFromStorageTab> = signal(RestoreTypeFromStorageTab.current);

  public computerName: WritableSignal<string> = signal('');
  public isOnline: WritableSignal<boolean> = signal(false);
  public isNBF: WritableSignal<boolean> = signal(false);
  public isLocallyLocalStorage: WritableSignal<boolean> = signal(false);
  public canRestoreToDifferentComputer: WritableSignal<boolean> = signal(false);
  public isRemote: WritableSignal<boolean> = signal(false);
  public selectedComputer: WritableSignal<Computer> = signal<Computer>(null);

  private hyperVMap: WritableSignal<Record<string, HyperVCheckState>> = signal({});

  public isOnlineSecondComputer: Signal<boolean> = computed(() => {
    const toComputer = this.selectedComputer();

    return toComputer?.online && Computer.isAgentOnline(toComputer, AgentType.Backup);
  });

  public isSupportTokenSecondComputer: Signal<boolean> = computed(() => {
    const toComputer = this.selectedComputer();

    return !!toComputer && Computer.IsSupportedAgentVersion(toComputer, AgentType.Backup, AgentVersionForUseToken);
  });

  public cloudRestore: Signal<boolean> = computed(() => this.cloudTypes.includes(this.restoreAs()));

  public restoreToHidden: Signal<boolean> = computed(() => this.isLocallyLocalStorage() || this.cloudRestore());

  public instantRestore: Signal<boolean> = computed(() => this.instantTypes.includes(this.restoreAs()));

  public needHyperV: Signal<boolean> = computed(() => this.hyperVTypes.includes(this.restoreAs()));

  public currentComputerHyperVMissing: Signal<boolean> = computed(() => this.needHyperV() && this.hyperVMissing(this.mainService.hid));

  public selectedComputerHyperVMissing: Signal<boolean> = computed(
    () => this.needHyperV() && this.hyperVMissing(this.selectedComputer()?.hid)
  );

  public currentComputerHyperVChecking: Signal<boolean> = computed(() => this.needHyperV() && this.hyperVChecking(this.mainService.hid));

  public selectedComputerHyperVChecking: Signal<boolean> = computed(
    () => this.needHyperV() && this.hyperVChecking(this.selectedComputer()?.hid)
  );

  private targetHid: Signal<string> = computed(() =>
    this.restoreTo() === RestoreTypeFromStorageTab.different ? this.selectedComputer()?.hid : this.mainService.hid
  );

  public loadingCheckHyperV: Signal<boolean> = computed(() => Object.values(this.hyperVMap()).some((state) => state.checking));

  private hyperVValid: Signal<boolean> = computed(() => {
    if (!this.needHyperV()) return true;
    if (this.loadingCheckHyperV()) return false;

    const state = this.hyperVMap()[this.targetHid()];

    return !state || state.installed;
  });

  public selectedTypeLabel: Signal<string> = computed(() => {
    const restoreAs = this.restoreAs();

    return this.restoreTypeTextMap[restoreAs] || 'Unknown type';
  });

  public requirements: Signal<string[]> = computed(() => this.requirementsTextMap[this.restoreAs()] || []);

  constructor() {
    this.initBaseData();
  }

  public restoreToChangeHandler(restoreTo: RestoreTypeFromStorageTab): void {
    this.restoreTo.set(restoreTo);

    if (restoreTo === RestoreTypeFromStorageTab.different) this.selectComputerClickHandler();
  }

  public selectComputerClickHandler(): void {
    this.modalFacade.showSelectComputerModal(this.modalSettings()).subscribe((result) => {
      this.selectedComputer.set(result?.items?.[0] || null);
    });
  }

  private hyperVMissing(hid: string): boolean {
    const state = hid ? this.hyperVMap()[hid] : null;

    return !!state && !state.checking && !state.installed;
  }

  private hyperVChecking(hid: string): boolean {
    return hid ? !!this.hyperVMap()[hid]?.checking : false;
  }

  private checkHyperV(hid: string, online: boolean): void {
    if (!this.needHyperV() || !hid || !online || this.hyperVMap()[hid]) return;

    this.setHyperVState(hid, { checking: true, installed: false });

    this.stepService
      .VMFeatureTest(hid, WindowsFeatures.hyperV)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.setHyperVState(hid, { checking: false, installed: !!res?.data }),
        error: () => this.setHyperVState(hid, { checking: false, installed: false })
      });
  }

  private setHyperVState(hid: string, state: HyperVCheckState): void {
    this.hyperVMap.update((prev) => ({ ...prev, [hid]: state }));
  }

  private modalSettings(): ModalSettings {
    return {
      size: MbsSize.md,
      data: {
        items: [],
        filter: this.needHyperV() ? { os: [OsType.windows] } : { os: [] },
        hiddenFilters: this.needHyperV()
          ? { minAppVersion: AgentVersionForUseTokenString, appIds: [AgentType.Backup], online: true }
          : {
              platformType: PlatformTypes.crossPlatform | PlatformTypes.netFramework,
              minAppVersion: AgentVersionForUseTokenString,
              appIds: [AgentType.Backup],
              online: true
            },
        disableOs: true,
        notSetResult: true,
        showComputerDetails: true,
        saveText: this.i18nPipe.transform('shared:buttons.ok'),
        selectedAll: false,
        multipleSelect: false,
        betaFeatureBuildName: BetaFeatureBuildName.BackupBuilds,
        betaFeatureName: BetaFeatureNameForRestoreToDifferentComputer,
        betaFeatureVersion: AgentVersionForUseToken,
        computerValidator: this.computerValidator,
        computerValidatorError: this.computerIncorrect,
        computersNotFound: this.computersNotFound,
        excludeItem: !this.instantRestore() && this.mainService.hid ? { fieldName: 'hid', value: this.mainService.hid } : null
      }
    };
  }

  private initBaseData() {
    this.computerName.set(this.mainService.compName);
    this.isOnline.set(!this.mainService.isOffline);
    this.isNBF.set(this.mainService.isNBF);
    this.isLocallyLocalStorage.set(this.mainService.restoreParams?.isLocallyLocalStorage);
    this.canRestoreToDifferentComputer.set(this.mainService.restoreParams?.canRestoreToDifferentComputer);
    this.isRemote.set(this.mainService.restoreParams?.isRemote);

    this.initEffects();
  }

  private initEffects() {
    effect(() => {
      if (!this.needHyperV()) return;

      const selectedComputer = this.selectedComputer();

      untracked(() => {
        this.checkHyperV(this.mainService.hid, this.isOnline());
        this.checkHyperV(selectedComputer?.hid, this.isOnlineSecondComputer());
      });
    });

    effect(() => {
      if (!this.cloudRestore()) return;

      untracked(() => {
        if (this.restoreTo() === RestoreTypeFromStorageTab.different) this.restoreTo.set(RestoreTypeFromStorageTab.current);
      });
    });

    effect(() => {
      const restoreTo = this.restoreTo();
      const selectedComputer = this.selectedComputer();
      const hyperVValid = this.hyperVValid();

      this.value.set({
        computer: selectedComputer,
        restoreTo: restoreTo,
        valid: (restoreTo !== RestoreTypeFromStorageTab.different || !!selectedComputer) && hyperVValid
      });
    });
  }
}
